/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import type { Plan } from '../../../services/subscription.service';

interface PlanComparisonProps {
  plans: Plan[];
  currentPlanId: number;
  onSelectPlan: (planId: number) => void;
}

const containerStyles = css`
  background: rgba(139, 92, 246, 0.03);
  border: 1px solid rgba(139, 92, 246, 0.1);
  border-radius: 12px;
  padding: 24px;
  backdrop-filter: blur(10px);
  box-sizing: border-box; min-width: 0;

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.05);
    border-color: rgba(139, 92, 246, 0.2);
  }

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const headerStyles = css`
  text-align: center;
  margin-bottom: 32px;

  h3 {
    margin: 0 0 8px 0;
    font-size: 24px;
    color: #ffffff;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
  }

  [data-theme="light"] & {
    h3 {
      color: #1F2933;
    }
    p {
      color: #6B7280;
    }
  }
`;

const plansGridStyles = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const planCardStyles = (isCurrent: boolean, isPopular: boolean) => css`
  border: 2px solid ${isCurrent ? 'rgba(139, 92, 246, 0.6)' : isPopular ? 'rgba(139, 92, 246, 0.4)' : 'rgba(139, 92, 246, 0.2)'};
  border-radius: 12px;
  padding: 24px;
  position: relative;
  transition: all 0.3s ease;
  background: ${isCurrent ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)'};
  display: flex;
  flex-direction: column;
  height: 100%;

  ${!isCurrent && `
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(139, 92, 246, 0.2);
      border-color: rgba(139, 92, 246, 0.5);
      background: rgba(139, 92, 246, 0.08);
    }
  `}

  [data-theme="light"] & {
    background: ${isCurrent ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)'};
    border-color: ${isCurrent ? 'rgba(139, 92, 246, 0.5)' : isPopular ? 'rgba(139, 92, 246, 0.35)' : 'rgba(139, 92, 246, 0.25)'};

    ${!isCurrent && `
      &:hover {
        box-shadow: 0 8px 24px rgba(139, 92, 246, 0.15);
        border-color: rgba(139, 92, 246, 0.4);
        background: rgba(139, 92, 246, 0.08);
      }
    `}
  }
`;

const badgeStyles = css`
  position: absolute;
  top: -12px;
  right: 24px;
  background: rgba(139, 92, 246, 0.95);
  border: 1px solid rgba(139, 92, 246, 1);
  color: #ffffff;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  backdrop-filter: blur(10px);

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.9);
    color: #FFFFFF;
  }
`;

const planHeaderStyles = css`
  text-align: center;
  margin-bottom: 20px;

  h4 {
    margin: 0 0 12px 0;
    font-size: 22px;
    color: #ffffff;
    font-weight: 600;
  }

  [data-theme="light"] & {
    h4 {
      color: #1F2933;
    }
  }
`;

const priceStyles = css`
  font-size: 36px;
  font-weight: bold;
  color: rgba(139, 92, 246, 1);

  span {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: normal;
  }

  [data-theme="light"] & {
    color: rgba(139, 92, 246, 1);
    span {
      color: #6B7280;
    }
  }
`;

const featureListStyles = css`
  list-style: none;
  padding: 0;
  margin: 20px 0 auto 0;
  min-height: 180px;
`;

const featureItemStyles = css`
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);

  &::before {
    content: '✓';
    display: inline-block;
    width: 18px;
    height: 18px;
    background: rgba(139, 92, 246, 0.4);
    color: #ffffff;
    border-radius: 50%;
    margin-right: 10px;
    flex-shrink: 0;
    text-align: center;
    line-height: 18px;
    font-weight: bold;
    font-size: 12px;
  }

  [data-theme="light"] & {
    color: rgba(31, 41, 51, 0.9);
    &::before {
      background: rgba(139, 92, 246, 0.3);
      color: #1F2933;
    }
  }
`;

const buttonStyles = (isCurrent: boolean) => css`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${isCurrent ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  margin-top: 20px;
  background: rgba(139, 92, 246, 0.2);
  border: 2px solid rgba(139, 92, 246, 0.5);
  color: #ffffff;
  flex-shrink: 0;

  ${isCurrent ? `
    opacity: 0.6;
    cursor: not-allowed;
  ` : `
    &:hover {
      transform: translateY(-2px);
      background: rgba(139, 92, 246, 0.3);
      border-color: rgba(139, 92, 246, 0.7);
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
    }
  `}

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.15);
    border-color: rgba(139, 92, 246, 0.4);
    color: #1F2933;

    ${isCurrent ? `
      opacity: 0.6;
    ` : `
      &:hover {
        background: rgba(139, 92, 246, 0.2);
        border-color: rgba(139, 92, 246, 0.6);
        box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
      }
    `}
  }
`;

const confirmModalStyles = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const modalContentStyles = css`
  background: rgba(5, 5, 16, 0.95);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 12px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(139, 92, 246, 0.3);
  backdrop-filter: blur(20px);

  h4 {
    margin: 0 0 16px 0;
    font-size: 20px;
    color: #ffffff;
    font-weight: 600;
  }

  p {
    margin: 0 0 24px 0;
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
  }

  [data-theme="light"] & {
    background: rgba(255, 255, 255, 0.98);
    border-color: rgba(139, 92, 246, 0.3);
    box-shadow: 0 8px 32px rgba(139, 92, 246, 0.2);

    h4 {
      color: #1F2933;
    }
    p {
      color: rgba(31, 41, 51, 0.8);
    }
  }
`;

const modalButtonsStyles = css`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const modalButtonBaseStyles = css`
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
`;

const cancelButtonStyles = css`
  ${modalButtonBaseStyles}
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.3);
  color: rgba(255, 255, 255, 0.8);

  &:hover {
    background: rgba(139, 92, 246, 0.15);
    border-color: rgba(139, 92, 246, 0.4);
  }

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.08);
    border-color: rgba(139, 92, 246, 0.25);
    color: #1F2933;

    &:hover {
      background: rgba(139, 92, 246, 0.12);
      border-color: rgba(139, 92, 246, 0.35);
    }
  }
`;

const confirmButtonStyles = css`
  ${modalButtonBaseStyles}
  background: rgba(139, 92, 246, 0.3);
  border: 2px solid rgba(139, 92, 246, 0.6);
  color: #ffffff;

  &:hover {
    transform: translateY(-2px);
    background: rgba(139, 92, 246, 0.4);
    border-color: rgba(139, 92, 246, 0.8);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
  }

  [data-theme="light"] & {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.5);
    color: #1F2933;

    &:hover {
      background: rgba(139, 92, 246, 0.3);
      border-color: rgba(139, 92, 246, 0.7);
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
    }
  }
`;

export const PlanComparison = ({ plans, currentPlanId, onSelectPlan }: PlanComparisonProps) => {
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleSelectPlan = (plan: Plan) => {
    setSelectedPlan(plan);
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (selectedPlan) {
      onSelectPlan(selectedPlan.id);
      setShowConfirmModal(false);
      setSelectedPlan(null);
    }
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setSelectedPlan(null);
  };

  const formatPrice = (amount: number) => {
    if (amount === 0) return t('common.free');
    return `$${amount}`;
  };

  const getFeatures = (plan: Plan) => {
    const features: string[] = [];
    features.push(t('subscription.creditsPerCycleFeature', { count: plan.creditsPerCycle }));
    if (plan.maxImages) features.push(t('subscription.imagesPerCycle', { count: plan.maxImages }));
    if (plan.maxVideoMinutes) features.push(t('subscription.videoPerCycle', { count: plan.maxVideoMinutes }));
    if (plan.storageLimitGb) features.push(t('subscription.storageGb', { count: plan.storageLimitGb }));
    return features;
  };

  const isPopular = (plan: Plan) => {
    const name = plan.name.toLowerCase();
    return name.includes('pro') || name.includes('personal') || name.includes('guardian');
  };
  const isCurrent = (plan: Plan) => plan.id === currentPlanId;

  // Sort plans by price
  const sortedPlans = [...plans].sort((a, b) => a.priceAmount - b.priceAmount);

  return (
    <>
      <div css={containerStyles}>
        <div css={headerStyles}>
          <h3>{t('subscription.comparePlans')}</h3>
          <p>{t('subscription.choosePlan')}</p>
        </div>

        <div css={plansGridStyles}>
          {sortedPlans.map((plan) => (
            <div key={plan.id} css={planCardStyles(isCurrent(plan), isPopular(plan))}>
              {isPopular(plan) && !isCurrent(plan) && <div css={badgeStyles}>{t('subscription.mostPopular')}</div>}
              {isCurrent(plan) && <div css={badgeStyles}>{t('subscription.currentPlan')}</div>}

              <div css={planHeaderStyles}>
                <h4>{plan.name}</h4>
                <div css={priceStyles}>
                  {formatPrice(plan.priceAmount)}
                  {plan.priceAmount > 0 && <span> {t('subscription.perMonth')}</span>}
                </div>
              </div>

              <ul css={featureListStyles}>
                {getFeatures(plan).map((feature, index) => (
                  <li key={index} css={featureItemStyles}>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                css={buttonStyles(isCurrent(plan))}
                onClick={() => handleSelectPlan(plan)}
                disabled={isCurrent(plan)}
              >
                {isCurrent(plan) ? t('subscription.currentPlan') : plan.priceAmount === 0 ? t('subscription.downgrade') : t('subscription.upgrade')}
              </button>
            </div>
          ))}
        </div>
      </div>

      {showConfirmModal && selectedPlan && (
        <div css={confirmModalStyles} onClick={handleCancel}>
          <div css={modalContentStyles} onClick={(e) => e.stopPropagation()}>
            <h4>{t('subscription.confirmPlanChange')}</h4>
            <p>
              <Trans
                i18nKey="subscription.confirmSwitch"
                values={{
                  plan: selectedPlan.name,
                  price: formatPrice(selectedPlan.priceAmount),
                  period: selectedPlan.priceAmount > 0 ? t('subscription.perMonthText') : '',
                }}
                components={{ strong: <strong /> }}
              />
            </p>
            <p>
              {selectedPlan.priceAmount > 0
                ? t('subscription.upgradeImmediate')
                : t('subscription.downgradeEnd')}
            </p>
            <div css={modalButtonsStyles}>
              <button css={cancelButtonStyles} onClick={handleCancel}>
                {t('common.cancel')}
              </button>
              <button css={confirmButtonStyles} onClick={handleConfirm}>
                {t('subscription.confirmChange')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
